// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getIsShareEntity } from "@insite/client-framework/ServerSideRendering";
import siteMessage from "@insite/client-framework/SiteMessage";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { useScript } from "@vanmeter/App/Hooks/useScript";
import { color, getSystemColor, hideEmpty, xScreen } from "@vanmeter/XDL/Utilities";
import { fontConfig } from "@vanmeter/XDL/constants";
import React, { useEffect } from "react";
import { css } from "styled-components";

type Props = {
    formId: number;
    munchkinId: string;
    baseUrl: string;
    hiddenFields?: Record<string, any>;
    formRef: React.RefObject<any>;
    isHiddenForm?: boolean;
    onSuccess?: () => void;
    onReady?: () => void;
};

export interface MarketoFormStyles {
    formWrapper?: InjectableCss;
}

const urlIcon = `"data:image/svg+xml,%3Csvg width='9' height='13' viewBox='0 0 9 6' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M4.5 6L0 0h9L4.5 6z' fill='%23${getSystemColor("common.ui5", 1, true).replace("#", "")}'/%3E%3C/svg%3E"`;
export const marketoFormStyles: MarketoFormStyles = {
    formWrapper: {
        css: css`
            ${({ isHiddenForm }: any) => isHiddenForm && `display:none;`}

            .mktoForm {
                ${xScreen("lte", "sm")} {
                    padding: 0;
                }
                input[type="text"],
                input[type="email"],
                input[type="tel"],
                textarea.mktoField,
                select.mktoField {
                    width: 100% !important;
                    height: 36px !important;
                    box-sizing: border-box;
                    font-size: ${fontConfig.smallSingleLine.size}px;
                    line-height: ${fontConfig.smallSingleLine.lineHeight};
                    color: ${color("text.main")} !important;
                    border: 1px solid ${color("common.ui5")} !important;
                    border-radius: 2px;
                    background-color: ${color("common.ui1")};
                    &:hover {
                        border-color: ${color("common.ui15")} !important;
                    }

                    &:focus {
                        border-color: ${color("common.ui16")} !important;
                        border-style: solid !important;
                        outline: none;
                    }
                    &.mktoInvalid,
                    &.mktoInvalid:focus {
                        border-color: ${color("danger.main")} !important;
                    }
                }

                input[type="text"],
                input[type="email"],
                input[type="tel"],
                textarea.mktoField {
                    padding: 0 8px !important;
                    box-shadow: inset 0 1px 0 0 ${color("common.black", 0.1)} !important;

                    &::placeholder {
                        font-style: italic;
                        color: ${color("common.ui4")};
                    }
                }

                textarea.mktoField {
                    height: auto !important;
                    min-height: 72px;
                    padding: 8px !important;
                    resize: vertical;
                }

                select.mktoField {
                    padding: 0 24px 0 8px !important;
                    background-image: url(${urlIcon});
                    background-repeat: no-repeat;
                    background-position: right 8px center;
                    background-size: 9px 6px;
                    appearance: none;
                    cursor: pointer;
                }

                .mktoFormRow {
                    margin-bottom: 8px;
                    ${hideEmpty}
                }

                .mktoFormCol,
                .mktoFieldWrap {
                    width: 100%;
                    .mktoLogicalField,
                    .mktoHtmlText {
                        width: 100% !important;
                    }
                }
                input[type="email"] ~ .mktoError {
                    bottom: -55px !important;
                }

                .mktoCheckboxList {
                    padding: 0;
                    label {
                        cursor: pointer;
                        font-size: ${fontConfig.xSmallSingleLine.size}px;
                        line-height: ${fontConfig.xSmallSingleLine.lineHeight};
                        color: ${color("text.main")};
                        margin: 0 0 12px 17px;
                    }
                    input[type="checkbox"] {
                        position: relative;
                        appearance: none;
                        width: 13px;
                        height: 13px;
                        border: 1px solid ${color("common.ui4")};
                        border-radius: 2px;
                        background: ${color("common.ui2")};
                        cursor: pointer;
                        &:checked {
                            background: ${color("common.ui16")};
                            border-color: ${color("common.ui16")};
                            &::after {
                                content: "";
                                position: absolute;
                                top: 50%;
                                left: 50%;
                                transform: translate(-50%, -50%);
                                width: 8px;
                                height: 7px;
                                background-color: ${color("common.ui1")};
                                mask-image: url("data:image/svg+xml,%3Csvg width='8' height='7' viewBox='0 0 8 7' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M7.77.1c.25.184.303.52.125.756L3.322 6.78a.653.653 0 0 1-.41.218.642.642 0 0 1-.465-.151L.161 4.692a.546.546 0 0 1 0-.774.633.633 0 0 1 .821 0l1.804 1.717L6.966.217A.616.616 0 0 1 7.77.099Z'/%3E%3C/svg%3E");
                                mask-size: contain;
                                mask-repeat: no-repeat;
                            }
                        }
                        &:hover {
                            border-color: ${color("common.ui15")};
                        }
                        &:checked:hover {
                            background: ${color("common.ui15")};
                            border-color: ${color("common.ui15")};
                        }
                    }
                }

                .mktoButtonRow {
                    display: none;
                }
                .mktoError {
                    right: unset !important;
                    bottom: -39px !important;
                    left: 0;
                    .mktoErrorArrowWrap {
                        top: -1px;
                        .mktoErrorArrow {
                            background: ${color("common.ui1")};
                            border: none;
                            box-shadow:
                                0 0 0 1px ${color("common.border")},
                                0 2px 4px 0 ${color("common.black", 0.1)};
                        }
                    }
                    .mktoErrorMsg {
                        font-size: ${fontConfig.xSmallSingleLine.size}px;
                        line-height: ${fontConfig.xSmallSingleLine.lineHeight};
                        color: ${color("text.main")};
                        background: ${color("common.ui1")};
                        border: none;
                        border-radius: 2px;
                        box-shadow:
                            0 0 0 1px ${color("common.border")},
                            0 2px 4px 0 ${color("common.black", 0.1)};
                        padding: 8px;
                        max-width: 250px;
                        text-shadow: none;
                    }
                }
                .mktoClear :empty {
                    display: none;
                }
            }
        `,
    },
};

const styles = marketoFormStyles;

const MarketoForm = ({
    formId,
    munchkinId,
    baseUrl,
    hiddenFields,
    onSuccess,
    onReady,
    formRef,
    isHiddenForm,
}: Props) => {
    const srcUrl = siteMessage("VM_formsScriptSource").toString();
    if (getIsShareEntity() || !srcUrl) {
        return null;
    }

    const status = useScript(srcUrl);

    // Initialize Marketo form when script is ready
    useEffect(() => {
        const MktoForms2 = (window as any)?.MktoForms2;
        if (!MktoForms2 || status !== "ready") return;

        let isMounted = true;

        MktoForms2.whenReady((form: any) => {
            if (!isMounted) return;
            if (form.getId() !== formId) return;

            formRef.current = form;

            if (hiddenFields) {
                form.addHiddenFields(hiddenFields);
            }
            form.offSuccess?.();
            form.onSuccess((values: any, followUpUrl: string) => {
                let isSamePage = false;
                if (followUpUrl) {
                    try {
                        const followUp = new URL(followUpUrl, window.location.href);
                        isSamePage =
                            followUp.origin === window.location.origin &&
                            followUp.pathname === window.location.pathname;
                    } catch {
                        isSamePage = false;
                    }
                }

                if (followUpUrl && !isSamePage) {
                    window.location.href = followUpUrl;
                    return false;
                }

                onSuccess?.();
                form.getFormElem()[0].reset();
                return false;
            });

            onReady?.();
        });

        MktoForms2.loadForm(baseUrl, munchkinId, formId);

        return () => {
            isMounted = false;
        };
    }, [formId, munchkinId, baseUrl, status]);

    useEffect(() => {
        if (formRef?.current && hiddenFields) {
            formRef.current.addHiddenFields(hiddenFields);
        }
    }, [hiddenFields]);

    return (
        <StyledWrapper {...styles.formWrapper} {...{ isHiddenForm }}>
            <form id={`mktoForm_${formId}`} />
        </StyledWrapper>
    );
};

export default MarketoForm;
